import { defineStore } from 'pinia';
import { deepCopy } from 'commons/utils';
import publishingCoreService from 'commons/api/publishingCoreService/publishingCoreService';
import compositionHelper from 'commons/composition/compositionHelper';
import { createInitialPosStateCounts } from 'commons/api/models/publishingCore/compositionPosStates';
import type { CompositionPosStateKey } from 'commons/api/models/publishingCore/compositionPosStates';
import type { PosStateCounts } from 'commons/api/publishingCoreService/publishingCoreService';
import type { Composition } from '@spreadgroup/common-partner-lib/composition';
import { usePublishingStore } from 'commons/stores/publishing';
import store from 'commons/store/Store';

/** Pagination metadata of the last fetched page, as reported by the API. */
type CompositionListMeta = {
  count: number | null;
  limit: number | null;
  offset: number | null;
};

type CompositionListFilter = {
  query: string | null;
  posState: CompositionPosStateKey | null;
  page: number;
  pageSize: number;
};

type State = {
  list: Composition[];
  meta: CompositionListMeta;
  filter: CompositionListFilter;
  posStateCounts: PosStateCounts;
  searchDeliveredResults: boolean;
};

const createDefaultFilter = (): CompositionListFilter => ({
  query: null,
  posState: null,
  page: 0,
  pageSize: 20,
});

export const useCompositionsStore = defineStore('compositions', {
  state: (): State => ({
    list: [],
    meta: {
      count: null,
      limit: null,
      offset: null,
    },
    filter: createDefaultFilter(),
    posStateCounts: createInitialPosStateCounts(),
    searchDeliveredResults: false,
  }),

  getters: {
    totalCompositionCount: (state): number | null => state.meta.count,
    searched: (state): boolean =>
      Boolean(state.filter.query || state.filter.posState),
    unsuccessfulSearch(state): boolean {
      return this.searched && !state.searchDeliveredResults;
    },
  },

  actions: {
    setList(compositions: Composition[], meta: CompositionListMeta) {
      this.list = compositions.map(
        compositionHelper.addEmptyDataStructureForDefaultMetaData,
      );
      this.meta = meta;
    },

    replaceComposition(composition: Composition) {
      const index = this.list.findIndex((i) => i.id === composition.id);
      // Not on the current page, e.g. detail page opened via a direct link
      if (index === -1) return;
      this.list[index] = deepCopy(composition);
    },

    setFilter(filter: Partial<CompositionListFilter>) {
      Object.assign(this.filter, filter);
    },

    resetFilter() {
      this.filter = createDefaultFilter();
    },

    async fetchCompositions() {
      const { query, pageSize, page, posState } = this.filter;

      const publishingStore = usePublishingStore();
      const scope = publishingStore.currentScope?.key;

      // Fetch compositions based on the current filter
      const response = await publishingCoreService.getCompositions({
        query: query || null,
        limit: pageSize,
        offset: pageSize * page,
        scope,
        posState,
      });

      let { list: compositions, count, limit, offset } = response;

      if (this.searched) {
        const hasResults = compositions?.length > 0;
        this.searchDeliveredResults = hasResults;

        // If no results for query, fallback to default compositions
        if (!hasResults) {
          const defaultResponse = await publishingCoreService.getCompositions({
            limit: pageSize,
            offset: 0,
            scope,
          });

          compositions = defaultResponse.list;
          count = defaultResponse.count;
          limit = defaultResponse.limit;
          offset = defaultResponse.offset;
        }
      }

      this.setList(compositions, { count, limit, offset });
      if (page === 0) {
        await this.fetchPosStateCounts();
      }
    },

    async deleteComposition(compositionId: string) {
      await publishingCoreService.deleteComposition(compositionId);
      if (this.list.length <= 1) {
        this.setFilter({ page: 0 });
      }
      await this.fetchCompositions();
      // publishingCore is still a vuex module
      store.dispatch('publishingCore/fetchPublishingIntents');
    },

    async fetchPosStateCounts(targetScope?: string) {
      const publishingStore = usePublishingStore();
      const { query } = this.filter;
      const scope = targetScope || publishingStore.currentScope?.key;
      const counts = await publishingCoreService.getPosStateCounts({
        scope,
        query,
      });
      Object.assign(this.posStateCounts, counts);
    },
  },
});
